import { Badge, Button, Modal, Spinner } from '@crm/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CalendarClock, CalendarX } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { EmptyState } from '@/components';
import { apiClient } from '@/lib/api-client';
import { SCHEDULE_ROUTES } from '@/modules/schedule/api';

export type PanelMode = 'list' | 'cancel' | 'reschedule';

interface AppointmentRow {
  id: string;
  startAt: string;
  status: string;
  unitName?: string;
  professionalName?: string | null;
  items?: { serviceName: string }[];
}

const TITLES: Record<PanelMode, string> = {
  list: 'Agendamentos do cliente',
  cancel: 'Cancelar agendamento',
  reschedule: 'Reagendar',
};

const STATUS_LABEL: Record<string, string> = {
  SCHEDULED: 'Agendado',
  CONFIRMED: 'Confirmado',
  IN_PROGRESS: 'Em atendimento',
  COMPLETED: 'Finalizado',
  CANCELED: 'Cancelado',
  NO_SHOW: 'Nao compareceu',
};

const ABERTOS = ['SCHEDULED', 'CONFIRMED', 'IN_PROGRESS'];

const dataHora = new Intl.DateTimeFormat('pt-BR', {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
});

function periodo() {
  const from = new Date();
  from.setFullYear(from.getFullYear() - 1);
  const to = new Date();
  to.setFullYear(to.getFullYear() + 1);

  return { from: from.toISOString(), to: to.toISOString() };
}

export function AppointmentsPanel({
  customerId,
  mode,
  onClose,
}: {
  customerId: string;
  mode: PanelMode;
  onClose: () => void;
}) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { from, to } = periodo();

  const { data, isLoading } = useQuery({
    queryKey: ['customer-appointments', customerId],
    queryFn: () =>
      apiClient.get<{ data: AppointmentRow[] }>(
        `/api/v1/appointments?customerId=${customerId}&from=${from}&to=${to}`,
      ),
  });

  const cancel = useMutation({
    mutationFn: (id: string) =>
      apiClient.post(`/api/v1/appointments/${id}/cancel`, { reason: 'Cancelado pelo atendimento' }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['customer-appointments'] }),
  });

  const todos = data?.data ?? [];
  const lista = mode === 'list' ? todos : todos.filter((item) => ABERTOS.includes(item.status));

  return (
    <Modal open onClose={onClose} title={TITLES[mode]} size="medium">
      {isLoading ? (
        <div className="flex justify-center py-8">
          <Spinner />
        </div>
      ) : lista.length === 0 ? (
        <EmptyState
          icon={<CalendarClock />}
          title={mode === 'list' ? 'Sem agendamentos' : 'Nenhum agendamento em aberto'}
        />
      ) : (
        <ul className="space-y-2">
          {lista.map((item) => (
            <li
              key={item.id}
              className="flex items-center justify-between gap-3 rounded-card border border-border p-3"
            >
              <div className="min-w-0">
                <p className="font-medium">{dataHora.format(new Date(item.startAt))}</p>
                <p className="truncate text-sm text-muted-foreground">
                  {item.items?.map((service) => service.serviceName).join(', ') || 'Sem servicos'}
                </p>
              </div>
              <div className="flex shrink-0 items-center gap-2">
                <Badge variant="secondary">{STATUS_LABEL[item.status] ?? item.status}</Badge>
                {mode === 'cancel' ? (
                  <Button
                    size="sm"
                    variant="destructive"
                    disabled={cancel.isPending}
                    onClick={() => cancel.mutate(item.id)}
                  >
                    <CalendarX className="h-4 w-4" />
                    Cancelar
                  </Button>
                ) : null}
                {mode === 'reschedule' ? (
                  <Button size="sm" onClick={() => navigate(SCHEDULE_ROUTES.detail(item.id))}>
                    Reagendar
                  </Button>
                ) : null}
              </div>
            </li>
          ))}
        </ul>
      )}
    </Modal>
  );
}
